import { useState, useRef } from 'react';
import PropTypes from 'prop-types';

// Image optimization constants
const MAX_DIMENSION = 1200; // Max width/height - covers retina displays at 800px canvas
const JPEG_QUALITY = 0.85; // Good balance of quality and file size

function ImageUploader({ onImageSelect, maxSizeMB = 10 }) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const handleFile = async (file) => {
    setError('');

    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG, PNG, etc.)');
      return;
    }

    // Validate file size
    if (file.size > maxSizeBytes) {
      setError(`Image must be smaller than ${maxSizeMB}MB`);
      return;
    }

    try {
      // Create ImageBitmap with EXIF orientation applied
      // This normalizes the image so portrait photos display correctly in canvas
      const bitmap = await createImageBitmap(file, {
        imageOrientation: 'from-image',
      });

      // Calculate target dimensions (resize if larger than MAX_DIMENSION)
      let targetWidth = bitmap.width;
      let targetHeight = bitmap.height;

      if (targetWidth > MAX_DIMENSION || targetHeight > MAX_DIMENSION) {
        const scale = MAX_DIMENSION / Math.max(targetWidth, targetHeight);
        targetWidth = Math.round(targetWidth * scale);
        targetHeight = Math.round(targetHeight * scale);
      }

      // Draw to canvas at target size (resizes large images)
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);

      // Convert to JPEG with optimized quality (always JPEG for smaller size)
      const base64 = canvas.toDataURL('image/jpeg', JPEG_QUALITY);

      setPreview(base64);
      // Pass image data with dimensions for smart grid recommendations
      onImageSelect({
        imageData: base64,
        width: targetWidth,
        height: targetHeight,
        aspectRatio: targetWidth / targetHeight,
      });
      bitmap.close();
    } catch (err) {
      // Fallback for older browsers: use FileReader without EXIF correction
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setPreview(base64);
        onImageSelect(base64);
      };
      reader.onerror = () => {
        setError('Failed to read file');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleClear = () => {
    setPreview(null);
    setError('');
    onImageSelect(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className="w-full">
      {!preview ? (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-puzzle-border hover:border-puzzle-text-secondary'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
          onKeyDown={(e) => e.key === 'Enter' && handleClick()}
          role="button"
          tabIndex={0}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleChange}
            className="hidden"
          />
          <svg
            className="mx-auto h-12 w-12 text-puzzle-text-secondary"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="mt-2 text-sm text-puzzle-text-secondary">
            <span className="font-semibold text-blue-600">Click to upload</span>
            {' '}
            or drag and drop
          </p>
          <p className="mt-1 text-xs text-puzzle-text-secondary">
            PNG, JPG, GIF up to
            {' '}
            {maxSizeMB}
            MB
          </p>
        </div>
      ) : (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="w-full max-h-64 object-contain rounded-lg border border-puzzle-border"
          />
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
            aria-label="Remove image"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}

ImageUploader.propTypes = {
  onImageSelect: PropTypes.func.isRequired,
  maxSizeMB: PropTypes.number,
};

ImageUploader.defaultProps = {
  maxSizeMB: 10,
};

export default ImageUploader;
